import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, Package, Save, Truck } from "lucide-react";
import { API, Empty, money } from "./shared";
import { RfqCard, humanCategory } from "../Materials";

const Kpi = ({ label, value, sub, testid, tone }) => (
  <div data-testid={testid} style={{ flex: "1 1 170px", background: "#fff", border: `1px solid ${tone === "warn" ? "#FCD34D" : "#E2E8F0"}`, borderRadius: 12, padding: "14px 16px" }}>
    <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: tone === "warn" ? "#B45309" : "#0F172A", marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
);

export function PartnerOverview({ user, setView, openJob }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/partners/me/overview`).then(r => setD(r.data)).catch(() => setD({ actions: [] })); }, []);
  if (!d) return <section className="content"><Empty text="Loading…"/></section>;
  return (
    <section className="content" data-testid="partner-overview">
      {!d.has_capabilities && (
        <div data-testid="partner-caps-nudge" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#FFFBEB", border: "1px solid #FDE68A", marginBottom: 18, fontSize: 13 }}>
          <AlertTriangle size={16} style={{ color: "#B45309" }}/> Add your capabilities so we can flag matching RFQs and email you when new ones are posted.
          <button className="secondary-btn" style={{ marginLeft: "auto" }} onClick={() => setView("capabilities")} data-testid="partner-caps-nudge-btn">Set capabilities <ArrowRight size={13}/></button>
        </div>
      )}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
        <Kpi label="Buyer auctions for you" value={d.live_auctions ?? 0} sub={`${d.open_job_bids || 0} with your bid`} testid="partner-kpi-auctions"/>
        <Kpi label="Live sub-RFQs" value={d.live_rfqs} sub={`${d.matching_rfqs} match your capabilities`} testid="partner-kpi-live"/>
        <Kpi label="My open quotes" value={d.open_quotes} testid="partner-kpi-quotes"/>
        <Kpi label="Win rate" value={d.win_rate_pct == null ? "—" : `${d.win_rate_pct}%`} sub={`${d.won} order(s) won`} testid="partner-kpi-winrate"/>
        <Kpi label="Orders to ship" value={(d.to_ship || 0) + (d.jobs_to_ship || 0)} tone={(d.to_ship || 0) + (d.jobs_to_ship || 0) ? "warn" : undefined} testid="partner-kpi-toship"/>
        <Kpi label="Payout pending" value={money(d.payout_pending_inr)} testid="partner-kpi-pending"/>
        <Kpi label="Paid this month" value={money(d.paid_this_month_inr)} testid="partner-kpi-paid"/>
      </div>
      <h3 style={{ fontSize: 15, margin: "0 0 10px" }}>Action needed</h3>
      {d.actions.length === 0 ? <Empty text="You're all caught up."/> : (
        <div style={{ display: "grid", gap: 8 }} data-testid="partner-actions">
          {d.actions.map(a => (
            <button key={`${a.kind}-${a.rfq_id}`} data-testid={`partner-action-${a.kind}-${a.rfq_id}`} onClick={() => (a.kind.endsWith("_job") ? openJob(a.rfq_id) : setView(a.kind === "dispatch" ? "partner-orders" : "job-board"))}
              style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: "11px 14px", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
              {a.kind.startsWith("dispatch") ? <Truck size={15} style={{ color: "#B45309" }}/> : <Package size={15} style={{ color: "#1d4ed8" }}/>}
              <span style={{ flex: 1 }}>{a.text}</span><ArrowRight size={13} style={{ color: "#94a3b8" }}/>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function useRfqs(params) {
  const [rfqs, setRfqs] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(() => axios.get(`${API}/material-rfqs`, { params }).then(r => setRfqs(r.data?.rfqs || [])).catch(() => setRfqs([])), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return [rfqs, load];
}

function RfqList({ rfqs, userId, reload, empty }) {
  const [open, setOpen] = useState(null);
  if (!rfqs) return <div className="mat-boot"><Loader2 className="mat-spin"/> Loading RFQs…</div>;
  if (!rfqs.length) return <Empty text={empty}/>;
  return (
    <section className="mat-list" data-testid="materials-list" style={{ padding: 0 }}>
      {rfqs.map(r => <RfqCard key={r.id} rfq={r} supplierId={userId} isOpen={open === r.id} onToggle={() => setOpen(x => (x === r.id ? null : r.id))} onBidPlaced={reload}/>)}
    </section>
  );
}

const AUCTION_STATUS = { live: "Live", closed: "Closed", awarded: "Awarded", dispatched: "Dispatched", completed: "Completed" };

function AuctionList({ jobs, openJob, empty, testid, bidIds }) {
  if (!jobs.length) return <Empty text={empty}/>;
  return (
    <div data-testid={testid} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ background: "#F8FAFC" }}><tr>
          {["Request", "Quantity", "Deliver to", "Status", "Closes", ""].map(h => <th key={h} style={{ padding: "9px 12px", fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: "#64748b", textAlign: "left" }}>{h}</th>)}
        </tr></thead>
        <tbody>{jobs.map(j => (
          <tr key={j.id} data-testid={`partner-auction-${j.id}`} className="admin-design-row" onClick={() => openJob(j)} style={{ cursor: "pointer" }}>
            <td style={{ padding: "10px 12px", fontSize: 12.5, borderTop: "1px solid #EEF2F7" }}><b>{j.title}</b><div style={{ fontSize: 11, color: "#64748b" }}>{j.stock}</div></td>
            <td style={{ padding: "10px 12px", fontSize: 12.5, borderTop: "1px solid #EEF2F7" }}>{Number(j.quantity).toLocaleString("en-IN")} {j.quantity_unit}</td>
            <td style={{ padding: "10px 12px", fontSize: 12.5, borderTop: "1px solid #EEF2F7" }}>{j.delivery_pincode ? `PIN ${j.delivery_pincode}` : (j.ship_to || "").slice(0, 30)}{j.need_by_date && <div style={{ fontSize: 11, color: "#B45309" }}>Need by {j.need_by_date}</div>}</td>
            <td style={{ padding: "10px 12px", fontSize: 12.5, borderTop: "1px solid #EEF2F7" }}><span className={`status ${j.status === "live" ? "live" : "completed"}`}>{AUCTION_STATUS[j.status] || j.status}</span></td>
            <td style={{ padding: "10px 12px", fontSize: 12, borderTop: "1px solid #EEF2F7", color: "#475569" }}>{j.auction_ends_at ? new Date(j.auction_ends_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
            <td style={{ padding: "10px 12px", fontSize: 12.5, borderTop: "1px solid #EEF2F7", color: "#1d4ed8", fontWeight: 600, whiteSpace: "nowrap" }}>{j.status === "live" ? (bidIds?.has(j.id) ? "✓ Bid placed · View →" : "Bid →") : "View →"}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export function PartnerJobBoard({ user, jobs = [], openJob }) {
  const [tab, setTab] = useState("auctions");
  const [bidIds, setBidIds] = useState(new Set());
  useEffect(() => { axios.get(`${API}/partners/me/job-bids`).then(r => setBidIds(new Set((r.data || []).map(b => b.job_id)))).catch(() => {}); }, [jobs]);
  const [onlyMatch, setOnlyMatch] = useState(false);
  const [rfqs, reload] = useRfqs(tab === "mine" ? { mine: true } : {});
  const shown = rfqs && onlyMatch ? rfqs.filter(r => r.matches_me) : rfqs;
  return (
    <section className="content" data-testid="partner-job-board">
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div className="fin-tabs" role="tablist" style={{ margin: 0 }}>
          {[["auctions", "Buyer auctions"], ["live", "Printer sub-RFQs"], ["mine", "My sub-RFQ quotes"]].map(([id, label]) => (
            <button key={id} role="tab" data-testid={`partner-board-tab-${id}`} className={tab === id ? "fin-tab active" : "fin-tab"} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
        {tab !== "auctions" && <label style={{ fontSize: 12.5, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input data-testid="partner-board-only-match" type="checkbox" checked={onlyMatch} onChange={e => setOnlyMatch(e.target.checked)} style={{ width: "auto" }}/> Only RFQs matching my capabilities
        </label>}
      </div>
      {tab === "auctions" ? (
        <AuctionList jobs={jobs.filter(j => ["live", "closed"].includes(j.status))} openJob={openJob} bidIds={bidIds} testid="partner-auction-list"
          empty="No buyer auctions for your category right now. Buyers' Procurement requests that match you appear here."/>
      ) : (
        <RfqList rfqs={shown} userId={user?.id} reload={reload}
          empty={tab === "live" ? "No live RFQs right now. New requests from printers will appear here." : "You haven't quoted on any live RFQs yet."}/>
      )}
    </section>
  );
}

const STAGES = [["awarded", "Escrow funded"], ["dispatched", "Dispatched"], ["received", "Received by printer"], ["paid", "Paid"]];
const stageIdx = (r) => (["released", "manual_paid"].includes(r.payout_status) ? 3 : Math.max(0, STAGES.findIndex(s => s[0] === r.status)));

const StageTracker = ({ r }) => {
  const idx = stageIdx(r);
  return (
    <div data-testid={`partner-order-stages-${r.id}`} style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "0 0 8px" }}>
      {STAGES.map(([k, label], i) => (
        <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999,
          background: i === idx ? "#047857" : i < idx ? "#ECFDF5" : "#F1F5F9", color: i === idx ? "#fff" : i < idx ? "#047857" : "#94a3b8", border: "1px solid transparent" }}>
          {i <= idx && <CheckCircle2 size={11}/>}{label}
        </span>
      ))}
    </div>
  );
};

export function PartnerOrders({ user, jobs = [], openJob, milestones }) {
  const [rfqs, reload] = useRfqs({ status: "awarded" });
  const won = jobs.filter(j => j.awarded_supplier_id === user?.id);
  return (
    <section className="content" data-testid="partner-orders">
      <h3 style={{ fontSize: 15, margin: "0 0 10px" }}>Buyer orders</h3>
      <div style={{ marginBottom: 24 }}><AuctionList jobs={won} openJob={openJob} testid="partner-job-orders" empty="No buyer orders won yet."/></div>
      {won.some(j => ["awarded", "dispatched"].includes(j.status)) && (
        <div data-testid="partner-job-milestones" className="ac-embed" style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 4px" }}>Post a production milestone on each buyer order before you can mark it dispatched.</p>
          {milestones}
        </div>
      )}
      <h3 style={{ fontSize: 15, margin: "0 0 10px" }}>Printer sub-RFQ orders</h3>
      {!rfqs ? <div className="mat-boot"><Loader2 className="mat-spin"/> Loading…</div> : rfqs.length === 0 ? <Empty text="No orders yet. Your won RFQs will show up here."/> : (
        <div style={{ display: "grid", gap: 14 }}>
          {rfqs.map(r => (
            <div key={r.id} data-testid={`partner-order-${r.id}`}>
              <StageTracker r={r}/>
              <RfqCard rfq={r} supplierId={user?.id} isOpen={false} onToggle={() => {}} onBidPlaced={reload}/>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const inp = { border: "1px solid #CBD5E1", borderRadius: 8, padding: "8px 10px", fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: "inherit" };
const Field = ({ label, children }) => <label style={{ display: "grid", gap: 5, fontSize: 12, color: "#475569", fontWeight: 600 }}>{label}{children}</label>;

export function PartnerCapabilities({ role }) {
  const [ref, setRef] = useState(null);
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const finishing = role === "finishing_partner";
  useEffect(() => {
    let live = true;
    axios.get(`${API}/partners/me/capabilities`).then(r => {
      if (!live) return;
      setRef(r.data);
      setC(prev => prev || r.data.capabilities || { categories: [], brands: "", gsm_min: "", gsm_max: "", moq: "", lead_time_days: "", max_sheet_size: "", daily_capacity: "", dispatch_pincode: "", service_area: "", notes: "", email_alerts: true });
    }).catch(() => setRef({ categories: [] }));
    return () => { live = false; };
  }, []);
  if (!c) return <section className="content"><Empty text="Loading…"/></section>;
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const toggleCat = (cat) => setC({ ...c, categories: c.categories.includes(cat) ? c.categories.filter(x => x !== cat) : [...c.categories, cat] });
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const save = async () => {
    if (c.dispatch_pincode && !/^\d{6}$/.test(c.dispatch_pincode)) { toast.error("Dispatch pincode must be 6 digits"); return; }
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/partners/me/capabilities`, { ...c, gsm_min: num(c.gsm_min), gsm_max: num(c.gsm_max), lead_time_days: num(c.lead_time_days), dispatch_pincode: c.dispatch_pincode || null });
      setC(data.capabilities);
      toast.success("Capabilities saved");
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map(x => `${(x.loc || []).slice(-1)[0]}: ${String(x.msg || "").replace(/^Value error, /, "")}`).join(" · ") : "Check the values and try again"); } finally { setBusy(false); }
  };
  return (
    <section className="content" data-testid="partner-capabilities">
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: "20px 22px", display: "grid", gap: 18, maxWidth: 980 }}>
        <div>
          <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>{finishing ? "Finishing processes you offer" : "Materials you supply"}</h3>
          <p style={{ margin: "0 0 10px", fontSize: 12.5, color: "#64748b" }}>RFQs in these categories get a "Matches you" badge, and we email you when new ones are posted.</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(ref?.categories || []).map(cat => {
              const on = c.categories.includes(cat);
              return <button key={cat} type="button" data-testid={`partner-cap-cat-${cat}`} onClick={() => toggleCat(cat)} aria-pressed={on}
                style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? "#1d4ed8" : "#CBD5E1"}`, background: on ? "#EAF1FF" : "#fff", color: on ? "#1d4ed8" : "#475569", transition: "background-color .15s" }}>
                {on ? "✓ " : ""}{humanCategory(cat)}</button>;
            })}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          {finishing ? (
            <>
              <Field label="Max sheet size"><input data-testid="partner-cap-sheet" style={inp} value={c.max_sheet_size} onChange={set("max_sheet_size")} placeholder="e.g. 28 x 40 in"/></Field>
              <Field label="Daily capacity"><input data-testid="partner-cap-capacity" style={inp} value={c.daily_capacity} onChange={set("daily_capacity")} placeholder="e.g. 20,000 sheets/day"/></Field>
            </>
          ) : (
            <>
              <Field label="Brands / mills carried"><input data-testid="partner-cap-brands" style={inp} value={c.brands} onChange={set("brands")} placeholder="e.g. ITC, JK, Century"/></Field>
              <Field label="GSM / micron range">
                <div style={{ display: "flex", gap: 6 }}>
                  <input data-testid="partner-cap-gsm-min" type="number" style={inp} value={c.gsm_min ?? ""} onChange={set("gsm_min")} placeholder="min"/>
                  <input data-testid="partner-cap-gsm-max" type="number" style={inp} value={c.gsm_max ?? ""} onChange={set("gsm_max")} placeholder="max"/>
                </div>
              </Field>
            </>
          )}
          <Field label="Minimum order (MOQ)"><input data-testid="partner-cap-moq" style={inp} value={c.moq} onChange={set("moq")} placeholder={finishing ? "e.g. 1,000 sheets" : "e.g. 500 kg"}/></Field>
          <Field label={finishing ? "Typical turnaround (days)" : "Lead time (days)"}><input data-testid="partner-cap-lead" type="number" style={inp} value={c.lead_time_days ?? ""} onChange={set("lead_time_days")} placeholder="3"/></Field>
          <Field label="Dispatch pincode"><input data-testid="partner-cap-pincode" style={inp} maxLength={6} value={c.dispatch_pincode || ""} onChange={e => setC({ ...c, dispatch_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="400001"/></Field>
          <Field label="Service area"><input data-testid="partner-cap-area" style={inp} value={c.service_area} onChange={set("service_area")} placeholder="e.g. Maharashtra, Gujarat"/></Field>
        </div>
        <Field label="Notes for printers"><textarea data-testid="partner-cap-notes" rows={2} style={inp} value={c.notes} onChange={set("notes")} placeholder="Stock held, certifications, special finishes…"/></Field>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
          <input data-testid="partner-cap-alerts" type="checkbox" checked={c.email_alerts !== false} onChange={e => setC({ ...c, email_alerts: e.target.checked })} style={{ width: "auto" }}/>
          Email me when a new RFQ matching these capabilities is posted
        </label>
        <div><button data-testid="partner-cap-save" className="primary-btn" disabled={busy} onClick={save}><Save size={14}/> {busy ? "Saving…" : "Save capabilities"}</button></div>
      </div>
    </section>
  );
}
